import API from '/b2b/common/helpers/API';
import { API_NAME } from '#config/runtimeconfig';
import { handleError } from '../common/services/helpers';
import { v4 as uuid } from 'uuid';

export const CHANNEL_EMAIL = 'email';
export const CHANNEL_SLACK = 'slack';
export const CHANNEL_TEAMS = 'teams';

/**
 * Org-level Slack workspace installation state plus whether the caller's
 * email resolves to a Slack workspace member (drives whether the Slack
 * toggle is enabled in the UI).
 * @returns {Promise<{
 *     installed: boolean,
 *     teamId?: string,
 *     teamName?: string,
 *     installedBy?: { email: string },
 *     currentUserResolved?: boolean,
 *     currentUserSlackId?: string,
 * }>}
 */
export const fetchSlackInstallation = () => {
    return API.get(API_NAME, '/slack/installation', {}).catch(handleError);
};

/**
 * Returns the static OAuth config the webapp uses to build its own authorize
 * URL: `{ authorizeUrl, clientId, redirectUri, scopes }`. The webapp owns the
 * state UUID and the popup lifecycle; webservices only does the code-for-token
 * exchange at the end.
 */
export const fetchSlackOAuthConfig = () => {
    return API.get(API_NAME, '/slack/oauth/config', {}).catch(handleError);
};

/**
 * Trades a Slack authorization code for a bot token (server-side) and writes
 * the workspace install row keyed by the caller's customerId. State has
 * already been verified locally by the caller.
 * @param {string} code
 */
export const exchangeSlackOAuth = code => {
    const options = { body: { code } };
    return API.post(API_NAME, '/slack/oauth/exchange', options).catch(handleError);
};

export const uninstallSlackWorkspace = () => {
    return API.post(API_NAME, '/slack/installation/uninstall', {}).catch(handleError);
};

/**
 * Org-level Microsoft Teams installation state plus whether the caller's email
 * resolves to a Teams user we can proactively message (drives whether the Teams
 * toggle is enabled in the UI). Mirrors {@link fetchSlackInstallation}.
 * @returns {Promise<{
 *     installed: boolean,
 *     teamName?: string,
 *     installedBy?: { email: string },
 *     currentUserResolved?: boolean,
 * }>}
 */
export const fetchTeamsInstallation = () => {
    return API.get(API_NAME, '/teams/installation', {}).catch(handleError);
};

/**
 * Returns the config the webapp uses to build its own Microsoft Teams / Azure AD
 * admin-consent URL: `{ authorizeUrl, clientId, redirectUri, scopes }`. Mirrors
 * {@link fetchSlackOAuthConfig}; the webapp owns the state UUID and popup.
 */
export const fetchTeamsOAuthConfig = () => {
    return API.get(API_NAME, '/teams/oauth/config', {}).catch(handleError);
};

/**
 * Trades a Microsoft Teams authorization code for a bot install (server-side)
 * and writes the install row keyed by the caller's customerId. State has already
 * been verified locally by the caller. Mirrors {@link exchangeSlackOAuth}.
 * @param {string} code
 */
export const exchangeTeamsOAuth = code => {
    const options = { body: { code } };
    return API.post(API_NAME, '/teams/oauth/exchange', options).catch(handleError);
};

export const uninstallTeamsWorkspace = () => {
    return API.post(API_NAME, '/teams/installation/uninstall', {}).catch(handleError);
};

/**
 * Runs the full connect popup flow for a chat channel (Slack or Teams):
 *  1. fetch authorize URL config
 *  2. open the authorize popup with a fresh UUID `state`
 *  3. wait for the `/connect` page (Routes.component.jsx) to postMessage
 *     `{ code, state }` back to us
 *  4. verify state echoes our UUID
 *  5. POST the code to the channel's exchange endpoint
 *
 * Returns the exchange response. Rejects if the popup is closed before
 * completion or the provider returns an error.
 *
 * @param {object} params
 * @param {string} params.label       Provider name used in error messages, e.g. 'Slack'.
 * @param {() => Promise<object>} params.fetchConfig  Resolves `{ authorizeUrl, clientId, redirectUri, scopes }`.
 * @param {(code: string) => Promise<object>} params.exchange  Trades the code for an install.
 * @param {string} [params.scopeSeparator]  Delimiter for the `scope` param. Slack wants a
 *     comma; Azure AD (Teams) requires spaces and rejects a comma-joined list (AADSTS70011).
 * @param {object} [params.extraParams]  Extra authorize-URL query params. Teams needs
 *     `response_type=code`, which Slack defaults but Azure AD demands (AADSTS900144).
 */
const runConnectOAuthPopup = ({
    label,
    fetchConfig,
    exchange,
    scopeSeparator = ',',
    extraParams = {},
}) => {
    // Open the popup synchronously in the click handler so browsers keep the
    // user-activation context. The location is set once the config arrives.
    const w = 600;
    const h = 700;
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
    const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
    const height = window.innerHeight || document.documentElement.clientHeight || screen.height;
    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;

    const child = window.open(
        'about:blank',
        '_blank',
        `scrollbars=yes,toolbar=0,status=0,width=${w / systemZoom},height=${
            h / systemZoom
        },top=${top},left=${left}`
    );
    if (!child) {
        return Promise.reject(new Error(`${label} OAuth popup was blocked by the browser.`));
    }

    return new Promise((resolve, reject) => {
        let timer;
        const oauthState = uuid();

        const callback = messageEvent => {
            const { origin, data, source } = messageEvent;
            if (source !== child) {
                return;
            }
            if (origin !== window.location.origin) {
                return;
            }
            let parsedData;
            try {
                parsedData = typeof data === 'string' ? JSON.parse(data) : data || {};
            } catch (e) {
                return;
            }
            const { state, code, error } = parsedData;
            if (!state || state !== oauthState) {
                return;
            }
            try {
                clearInterval(timer);
                window.removeEventListener('message', callback);
            } catch (e) {
                // Already removed
            }
            if (error) {
                reject(new Error(`${label} OAuth denied: ${error}`));
                return;
            }
            if (!code) {
                reject(new Error(`${label} OAuth callback was missing the authorization code.`));
                return;
            }
            exchange(code).then(resolve).catch(reject);
        };
        window.addEventListener('message', callback);

        timer = setInterval(() => {
            if (child.closed) {
                try {
                    window.removeEventListener('message', callback);
                } catch (e) {
                    // Already removed
                }
                clearInterval(timer);
                reject(
                    new Error(`${label} OAuth window was closed before authorization completed.`)
                );
            }
        }, 500);

        fetchConfig()
            .then(config => {
                const { authorizeUrl, clientId, redirectUri, scopes } = config || {};
                if (!authorizeUrl || !clientId || !redirectUri || !scopes?.length) {
                    window.removeEventListener('message', callback);
                    clearInterval(timer);
                    try {
                        child.close();
                    } catch (e) {
                        // Ignore
                    }
                    reject(new Error(`${label} OAuth is not configured on the server.`));
                    return;
                }
                const uri = new URL(authorizeUrl);
                uri.searchParams.set('client_id', clientId);
                uri.searchParams.set('scope', scopes.join(scopeSeparator));
                uri.searchParams.set('redirect_uri', redirectUri);
                uri.searchParams.set('state', oauthState);
                Object.entries(extraParams).forEach(([key, value]) => {
                    uri.searchParams.set(key, value);
                });
                try {
                    child.location.href = uri.href;
                } catch (e) {
                    window.removeEventListener('message', callback);
                    clearInterval(timer);
                    try {
                        child.close();
                    } catch (_) {
                        // Ignore
                    }
                    reject(e);
                }
            })
            .catch(err => {
                window.removeEventListener('message', callback);
                clearInterval(timer);
                try {
                    child.close();
                } catch (e) {
                    // Ignore
                }
                reject(err);
            });
    });
};

/**
 * Runs the full Slack OAuth popup flow and returns `{ ok, teamId, teamName }`.
 * @see runConnectOAuthPopup
 */
export const connectSlackOAuth = () =>
    runConnectOAuthPopup({
        label: 'Slack',
        fetchConfig: fetchSlackOAuthConfig,
        exchange: exchangeSlackOAuth,
    });

/**
 * Runs the full Microsoft Teams connect (admin-consent) popup flow.
 * @see runConnectOAuthPopup
 */
export const connectTeamsOAuth = () =>
    runConnectOAuthPopup({
        label: 'Microsoft Teams',
        fetchConfig: fetchTeamsOAuthConfig,
        exchange: exchangeTeamsOAuth,
        scopeSeparator: ' ',
        // eslint-disable-next-line camelcase
        extraParams: { response_type: 'code' },
    });

/**
 * Per-channel delivery prefs for the caller. Returns every known channel
 * with defaults applied (email=true, all others=false) so the UI doesn't
 * have to know what 'absent row' means.
 * @returns {Promise<{ channels: Array<{ channel: string, enabled: boolean }> }>}
 */
export const fetchDeliveryPrefs = () => {
    return API.get(API_NAME, '/user/prefs/delivery', {}).catch(handleError);
};

export const updateDeliveryPref = ({ channel, enabled }) => {
    const options = { body: { channel, enabled } };
    return API.put(API_NAME, '/user/prefs/delivery', options).catch(handleError);
};
